import { t } from '../../lib/i18n';
import { DocumentVerificationStatus, type DocumentWithUser } from './types';

/**
 * Status vocabulary for the KYC queue — `Redesign-D-Admin{,-Mobile}`, screen
 * «Документи».
 *
 * The artboard says a status three times over: a left stripe on the row
 * (`box-shadow: inset 3px 0 0 …`), a mono uppercase chip, and — in the detail
 * panel — the tint behind the verdict. They are one decision, so they live in
 * one table here instead of being re-derived at three call sites.
 *
 * `expired` has no artboard: the export only draws pending / approved /
 * rejected. It is given the neutral fill rather than invented a colour, so a
 * document the API marks expired reads as "not actionable" and not as a fourth
 * status the design never agreed to.
 */

export interface StatusTone {
  /** Chip text, in the artboard's wording. */
  label: string;
  /** Chip background + ink. */
  chip: string;
  /** The row's 3px left rule. */
  stripe: string;
}

const TONE: Record<DocumentVerificationStatus, { chip: string; stripe: string }> = {
  [DocumentVerificationStatus.PENDING]: {
    chip: 'bg-cf-warn-tint text-cf-warn',
    stripe: 'shadow-[inset_3px_0_0_var(--color-cf-accent)]',
  },
  [DocumentVerificationStatus.VERIFIED]: {
    chip: 'bg-cf-success-tint text-cf-success',
    stripe: 'shadow-[inset_3px_0_0_var(--color-cf-success)]',
  },
  [DocumentVerificationStatus.REJECTED]: {
    chip: 'bg-cf-danger-tint text-cf-danger',
    stripe: 'shadow-[inset_3px_0_0_var(--color-cf-danger)]',
  },
  [DocumentVerificationStatus.EXPIRED]: {
    chip: 'bg-cf-fill text-cf-ink-subtle',
    stripe: 'shadow-[inset_3px_0_0_var(--color-cf-ink-faint)]',
  },
};

const LABEL_KEY: Record<DocumentVerificationStatus, string> = {
  [DocumentVerificationStatus.PENDING]: 'pending',
  [DocumentVerificationStatus.VERIFIED]: 'approved',
  [DocumentVerificationStatus.REJECTED]: 'rejected',
  [DocumentVerificationStatus.EXPIRED]: 'expired',
};

export function statusTone(status: DocumentVerificationStatus): StatusTone {
  const tone = TONE[status] ?? TONE[DocumentVerificationStatus.PENDING];
  return {
    label: t(`verification.admin.docs.status.${LABEL_KEY[status] ?? 'pending'}`),
    chip: tone.chip,
    stripe: tone.stripe,
  };
}

/** The mono uppercase status chip both viewports draw (9px mobile, 10px desktop). */
export function StatusChip({ status }: { status: DocumentVerificationStatus }) {
  const tone = statusTone(status);
  return (
    <span
      className={`inline-flex w-fit rounded-cf-sm px-[7px] py-[3px] font-mono text-[9px] tracking-[0.08em] whitespace-nowrap uppercase lg:px-2 lg:py-1 lg:text-[10px] ${tone.chip}`}
    >
      {tone.label}
    </span>
  );
}

/**
 * Byte count as MB. Kept byte-identical to the ported web helper (including the
 * English "Unknown size" sentinel) because `document-status.test.ts` pins both
 * branches — the UI decides what to show for an unknown size, not this.
 */
export function formatFileSize(bytes?: number): string {
  if (!bytes) return 'Unknown size';
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(2)} MB`;
}

// ── Display projection ──────────────────────────────────────────────────────
//
// The artboard's rows read «who · type · file · when». Those four strings are
// assembled from the API document in one place so the mobile card, the desktop
// row and the detail panel cannot drift apart.

const DASH = '—';

/** Whose document this is: the account name, falling back to the one on the scan. */
export function documentWho(doc: DocumentWithUser): string {
  const fromUser = [doc.user?.firstName, doc.user?.lastName].filter(Boolean).join(' ').trim();
  if (fromUser) return fromUser;
  const fromDoc = [doc.firstName, doc.lastName].filter(Boolean).join(' ').trim();
  return fromDoc || doc.user?.email || DASH;
}

/** The uploaded file's name; the document number stands in when it is absent. */
export function documentFile(doc: DocumentWithUser): string {
  return doc.fileName || doc.documentNumber || DASH;
}

/**
 * «2 год тому» — the artboard prints the age of the upload, not its timestamp,
 * because the queue is worked by how long a client has been waiting.
 */
export function documentWhen(doc: DocumentWithUser, now: Date = new Date()): string {
  if (!doc.uploadedAt) return DASH;
  const uploaded = new Date(doc.uploadedAt);
  if (Number.isNaN(uploaded.getTime())) return DASH;

  const seconds = Math.round((uploaded.getTime() - now.getTime()) / 1000);
  const format = new Intl.RelativeTimeFormat('uk-UA', { numeric: 'auto', style: 'narrow' });
  const scale: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 31_536_000],
    ['month', 2_592_000],
    ['day', 86_400],
    ['hour', 3_600],
    ['minute', 60],
  ];
  for (const [unit, size] of scale) {
    if (Math.abs(seconds) >= size) return format.format(Math.round(seconds / size), unit);
  }
  return format.format(Math.round(seconds), 'second');
}

/** One key/value line in the detail panel's check list. */
export interface DocumentCheck {
  key: string;
  value: string;
  tone: string;
}

/**
 * The detail panel's check list.
 *
 * The artboard fills it with a human verifier's verdicts — «Читабельність: ок»,
 * «Дублікати: немає». The API stores no such verdicts, so inventing them would
 * be inventing a number. These are the facts the record actually carries, in
 * the same four-to-five row shape.
 */
export function documentChecks(doc: DocumentWithUser): DocumentCheck[] {
  const rows: DocumentCheck[] = [
    { key: t('verification.admin.docs.checks.client'), value: documentWho(doc), tone: 'text-cf-ink' },
    {
      key: t('verification.admin.docs.checks.email'),
      value: doc.user?.email || DASH,
      tone: doc.user?.email ? 'text-cf-ink' : 'text-cf-ink-subtle',
    },
    {
      key: t('verification.admin.docs.checks.number'),
      value: doc.documentNumber || DASH,
      tone: doc.documentNumber ? 'text-cf-ink' : 'text-cf-ink-subtle',
    },
    {
      key: t('verification.admin.docs.checks.size'),
      value: doc.fileSize ? formatFileSize(doc.fileSize) : DASH,
      tone: doc.fileSize ? 'text-cf-ink' : 'text-cf-ink-subtle',
    },
    {
      key: t('verification.admin.docs.checks.uploaded'),
      value: doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString('uk-UA') : DASH,
      tone: doc.uploadedAt ? 'text-cf-ink' : 'text-cf-ink-subtle',
    },
  ];
  return rows;
}
